import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookingMessages } from "@/components/BookingMessages";
import { DamageClaimDialog } from "@/components/DamageClaimDialog";
import { toast } from "sonner";
import { Plus, Trash2, MessageCircle, ShieldCheck, Banknote, AlertTriangle, Camera } from "lucide-react";

export default function HostDashboard() {
  const { user, loading } = useAuth();
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [msgBooking, setMsgBooking] = useState(null);
  const [claimBooking, setClaimBooking] = useState(null);
  const [connect, setConnect] = useState(null);
  const navigate = useNavigate();

  const loadConnect = React.useCallback(() => {
    api.get("/host/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect({ connected: false, charges_enabled: false }));
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/listings/mine").then((r) => setListings(r.data)).catch(() => {});
    api.get("/bookings/hosting").then((r) => setBookings(r.data)).catch(() => {});
    loadConnect();
    const params = new URLSearchParams(window.location.search);
    const ret = params.get("connect");
    if (ret === "return") {
      toast.success("Thanks! We're confirming your payout details with Stripe…");
      setTimeout(loadConnect, 2500); // give Stripe a moment to propagate capabilities
      window.history.replaceState({}, "", "/host");
    } else if (ret === "refresh") {
      toast.info("Onboarding was interrupted — tap 'Set up bank payouts' to pick up where you left off.");
      window.history.replaceState({}, "", "/host");
    }
  }, [user, loading, navigate, loadConnect]);

  const remove = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/listings/${id}`);
      setListings((ls) => ls.filter((l) => l.id !== id));
      toast.success("Listing deleted");
    } catch { toast.error("Delete failed"); }
  };

  const refreshListings = () => api.get("/listings/mine").then((r) => setListings(r.data)).catch(() => {});
  const publishListing = async (id) => {
    try { await api.post(`/listings/${id}/submit`); toast.success("Submitted for review — we'll email you once it's live."); refreshListings(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };
  const pauseListing = async (id) => {
    try { await api.post(`/listings/${id}/pause`); toast.success("Listing paused — hidden from search."); refreshListings(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Pause failed"); }
  };
  const unpauseListing = async (id) => {
    try { await api.post(`/listings/${id}/unpause`); toast.success("Listing is live again."); refreshListings(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
  };

  const earnings = bookings
    .filter((b) => b.payment_status === "paid" || b.payment_status === "partially_refunded")
    .reduce((s, b) => s + (b.host_earnings ?? b.subtotal), 0)
    + bookings.reduce((s, b) => s + (b.deposit_claim_amount || 0), 0);

  const releaseDeposit = async (id) => {
    if (!window.confirm("Refund this renter's security deposit? This cannot be undone.")) return;
    try {
      await api.post(`/bookings/${id}/release-deposit`);
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, deposit_status: "released" } : b));
      toast.success("Deposit released to renter");
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
  };

  const setupPayouts = async () => {
    // Stripe refuses to render inside the Emergent Preview iframe (X-Frame-Options),
    // so open onboarding in a top-level tab. Open synchronously to dodge popup blockers.
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post("/host/connect/onboard", { origin_url: window.location.origin });
      if (win) {
        win.location.href = data.url;
        toast.info("Stripe onboarding opened in a new tab — complete it there, then return here.");
      } else {
        // Popup blocked: fall back to a top-level navigation that breaks out of the iframe.
        window.open(data.url, "_blank", "noopener") || (window.top || window).location.assign(data.url);
      }
    } catch (e) {
      if (win) win.close();
      const detail = e?.response?.data?.detail;
      if (detail === "CONNECT_NOT_ENABLED" || detail === "CONNECT_V1_DISABLED") {
        toast.info("Automatic bank payouts aren't fully switched on yet. Until then, your earnings are tracked in the ledger below.");
      } else {
        toast.error("Could not start payout setup");
      }
    }
  };

  const flagDamage = async (id, flagged) => {
    try {
      await api.post(`/bookings/${id}/flag-damage`, { flagged });
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, damage_flagged: flagged } : b));
      toast.success(flagged ? "Damage flagged — deposit held for review" : "Damage flag removed");
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">HOST</div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl">Dashboard</h1>
        </div>
        <Button onClick={() => navigate("/host/new")} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-11 gap-2" data-testid="new-listing-btn">
          <Plus className="w-4 h-4" /> New listing
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { k: listings.length, v: "Listings" },
          { k: bookings.length, v: "Total bookings" },
          { k: bookings.filter((b) => b.status === "confirmed").length, v: "Confirmed" },
          { k: `$${earnings.toFixed(0)}`, v: "Earnings (paid)" },
        ].map((s) => (
          <div key={s.v} className="border border-neutral-200 bg-white p-6 rounded-xl">
            <div className="font-display font-black text-3xl">{s.k}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      {/* Payouts */}
      <div className="border border-neutral-200 bg-white rounded-xl p-6 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="payouts-card">
        <div className="flex items-start gap-3">
          <Banknote className="w-6 h-6 text-[#FF4500] mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-bold text-lg">Payouts</div>
            {connect?.charges_enabled ? (
              <div className="text-sm text-neutral-600 mt-0.5">Bank payouts active — your share is wired to your bank automatically after each booking.</div>
            ) : (connect?.connected && connect?.onboarded) ? (
              <div className="text-sm text-neutral-600 mt-0.5">Your details were submitted — Stripe is verifying your account. Payouts switch on automatically once approved. Tap Refresh in a moment to check.</div>
            ) : (
              <div className="text-sm text-neutral-600 mt-0.5">
                You've earned <span className="font-semibold">${earnings.toFixed(2)}</span> so far, tracked in your SpareHours ledger. Connect a bank to get it wired automatically.
              </div>
            )}
          </div>
        </div>
        {connect?.charges_enabled ? (
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF4500] bg-[#FF4500]/10 px-3 py-1.5 rounded-full" data-testid="payouts-active">Bank connected</span>
        ) : (connect?.connected && connect?.onboarded) ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full" data-testid="payouts-pending">Verifying…</span>
            <Button onClick={loadConnect} variant="outline" className="rounded-full h-9 text-sm" data-testid="refresh-payouts-btn">Refresh</Button>
          </div>
        ) : (
          <Button onClick={setupPayouts} className="bg-[#0A0A0A] text-white hover:bg-neutral-800 rounded-full shrink-0" data-testid="setup-payouts-btn">
            Set up bank payouts
          </Button>
        )}
      </div>

      <Tabs defaultValue="listings">
        <TabsList>
          <TabsTrigger value="listings" data-testid="tab-listings">My listings</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
        </TabsList>
        <TabsContent value="listings" className="mt-6">
          {listings.length === 0 ? (
            <div className="border border-dashed border-neutral-300 rounded-xl p-16 text-center">
              <div className="font-display font-bold text-xl">No listings yet</div>
              <div className="text-sm text-neutral-500 mt-2 mb-6">Turn your empty hours into revenue.</div>
              <Button onClick={() => navigate("/host/new")} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full">Create your first listing</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((l) => (
                <div key={l.id} className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
                  <div className="aspect-[4/3] bg-neutral-100">
                    {l.photos?.[0] && <img src={imgSrc(l.photos[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-4">
                    <div className="font-display font-bold truncate">{l.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{l.city} · ${l.price_per_hour}/hr</div>
                    <span className={`inline-block mt-2 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${
                      l.status === "live" || l.status === "approved" ? "bg-[#FF4500]/10 text-[#FF4500]" :
                      l.status === "rejected" || l.status === "suspended" ? "bg-red-100 text-red-600" :
                      l.status === "needs_changes" ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-500"
                    }`} data-testid={`listing-status-${l.id}`}>
                      {(l.status || "live").replace("_", " ")}
                    </span>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to={`/spaces/${l.id}`} className="flex-1 min-w-[64px] text-center py-2 border border-neutral-300 rounded-full text-sm hover:border-neutral-900" data-testid={`preview-listing-${l.id}`}>
                        {(l.status === "live" || l.status === "approved") ? "View" : "Preview"}
                      </Link>
                      <button onClick={() => navigate(`/host/edit/${l.id}`)} className="flex-1 min-w-[64px] text-center py-2 border border-neutral-300 rounded-full text-sm hover:border-neutral-900" data-testid={`edit-listing-${l.id}`}>Edit</button>
                      {(l.status === "draft" || l.status === "needs_changes" || l.status === "rejected") && (
                        <button onClick={() => publishListing(l.id)} className="flex-1 min-w-[64px] text-center py-2 bg-[#FF4500] text-white rounded-full text-sm hover:bg-[#E63E00]" data-testid={`publish-listing-${l.id}`}>Submit</button>
                      )}
                      {(l.status === "live" || l.status === "approved") && (
                        <button onClick={() => pauseListing(l.id)} className="flex-1 min-w-[64px] text-center py-2 border border-neutral-300 rounded-full text-sm hover:border-neutral-900" data-testid={`pause-listing-${l.id}`}>Pause</button>
                      )}
                      {l.status === "paused" && (
                        <button onClick={() => unpauseListing(l.id)} className="flex-1 min-w-[64px] text-center py-2 bg-[#FF4500] text-white rounded-full text-sm hover:bg-[#E63E00]" data-testid={`unpause-listing-${l.id}`}>Resume</button>
                      )}
                      <button onClick={() => remove(l.id)} className="p-2 border border-neutral-300 rounded-full hover:border-red-500 hover:text-red-500" data-testid={`delete-listing-${l.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <div className="text-neutral-500 text-sm">No bookings yet.</div>
          ) : (
            <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  <tr>
                    <th className="text-left p-4">Space</th>
                    <th className="text-left p-4">Renter</th>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Time</th>
                    <th className="text-right p-4">You earn</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-neutral-100">
                      <td className="p-4">{b.listing_title}</td>
                      <td className="p-4">{b.renter_name}</td>
                      <td className="p-4 font-mono text-xs">{b.date}</td>
                      <td className="p-4 font-mono text-xs">{b.start_time}–{b.end_time}</td>
                      <td className="p-4 text-right font-display font-bold">${(b.host_earnings ?? b.subtotal).toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full ${
                          b.status === "confirmed" ? "bg-[#FF4500]/10 text-[#FF4500]" :
                          b.status === "pending" ? "bg-amber-100 text-amber-700" :
                          b.status === "cancelled" ? "bg-neutral-100 text-neutral-500" : "bg-neutral-100"
                        }`}>{b.status}</span>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <Button variant="outline" size="sm" onClick={() => setMsgBooking(b)} className="gap-1.5" data-testid={`message-renter-${b.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /> Message
                        </Button>
                        {b.deposit > 0 && b.deposit_status === "held" && (
                          <>
                            <Button size="sm" onClick={() => releaseDeposit(b.id)} className="ml-2 bg-[#0A0A0A] text-white hover:bg-neutral-800 gap-1.5" data-testid={`release-deposit-${b.id}`}>
                              <ShieldCheck className="w-3.5 h-3.5" /> Release ${b.deposit.toFixed(0)}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setClaimBooking(b)} className="ml-2 gap-1.5" data-testid={`file-claim-${b.id}`}>
                              <Camera className="w-3.5 h-3.5" /> File claim
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => flagDamage(b.id, !b.damage_flagged)} className={`ml-2 gap-1.5 ${b.damage_flagged ? "border-amber-500 text-amber-600" : ""}`} data-testid={`flag-damage-${b.id}`}>
                              <AlertTriangle className="w-3.5 h-3.5" /> {b.damage_flagged ? "Flagged" : "Flag damage"}
                            </Button>
                          </>
                        )}
                        {b.deposit > 0 && b.deposit_status === "released" && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-neutral-400">deposit refunded</span>
                        )}
                        {b.deposit > 0 && b.deposit_status === "claimed" && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-amber-600" data-testid={`claimed-${b.id}`}>claim ${(b.deposit_claim_amount || 0).toFixed(0)} kept</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <BookingMessages
        booking={msgBooking}
        open={!!msgBooking}
        onOpenChange={(o) => !o && setMsgBooking(null)}
        counterpart="Chat with renter"
      />
      <DamageClaimDialog
        booking={claimBooking}
        open={!!claimBooking}
        onOpenChange={(o) => !o && setClaimBooking(null)}
        onDone={(id, amt) => setBookings((bs) => bs.map((b) => b.id === id ? { ...b, deposit_status: "claimed", deposit_claim_amount: amt } : b))}
      />
    </div>
  );
}
